import { useEffect, useState, useMemo } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Header, { getInitialDarkMode } from "./components/Header";
import Footer from "./components/Footer";
import SEO from "./components/SEO";
import seoData from "./components/seoData";
import Home from "./pages/Home";
import SearchResults from "./pages/SearchResults";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiesPolicy from "./pages/CookiesPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import AffiliateDisclosure from "./pages/AffiliateDisclosure";
import Disclaimer from "./pages/Disclaimer";
import SuperDeals from "./pages/SuperDeals";
import AboutUs from "./pages/AboutUs";
import ContactUs from "./pages/ContactUs";
import AdShortcode from "./components/AdShortcode";
import SuperDealsManager from "./components/SuperDealsManager";
import TourPackagesPage from "./pages/TourPackagesPage";
import WebStory from "./pages/WebStory";
import CategoryPage from "./pages/CategoryPage";
import Wishlistfavorites from "./pages/Wishlistfavorites";
import AdvertiseWithUs from "./pages/AdvertiseWithUs";
import Jobs from "./pages/Jobs";
const routerBasename =
  typeof import.meta !== "undefined"
    ? (import.meta.env.BASE_URL || "/").replace(/\/+$/, "") || "/"
    : "/";
const SEO_OVERRIDES_STORAGE_KEY = "trw-tour-seo-meta-v1";

function readSeoOverrides() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(SEO_OVERRIDES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function getWebStorySeoFallback() {
  return seoData.webStory || {
    title: "Travel Web Stories & Visual Stories | Travel Rethink Ways",
    description: "Explore visual travel stories, destination highlights, and quick travel inspiration from Travel Rethink Ways.",
    canonical: "https://travel.rethinkways.com/visual-stories",
    image: "https://travel.rethinkways.com/wp-content/uploads/2025/12/travel-rethink-ways-feature-img.jpg",
  };
}

function mergeSeoOverride(baseSeo, overrideKey) {
  if (!baseSeo) return null;
  const override = readSeoOverrides()?.[overrideKey] || {};
  return {
    ...baseSeo,
    title: typeof override.title === "string" && override.title.trim() ? override.title.trim() : baseSeo.title,
    description: typeof override.description === "string" && override.description.trim() ? override.description.trim() : baseSeo.description,
  };
}

function getEmbeddedRoute() {
  if (typeof window === "undefined") {
    return null;
  }

  const route = window.__TRW_EMBED_ROUTE__;
  return typeof route === "string" && route.trim() ? route.trim() : null;
}

function splitRoute(route) {
  const [pathnamePart, searchPart = ""] = route.split("?");

  return {
    pathname: pathnamePart.startsWith("/") ? pathnamePart : `/${pathnamePart}`,
    search: searchPart ? `?${searchPart}` : "",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure helper — resolves the correct SEO object for any route.
// Returns null for dynamic routes that manage their own SEO (e.g. /category/:slug).
// ─────────────────────────────────────────────────────────────────────────────
function resolveSeo(pathname, search) {
  const searchParams = new URLSearchParams(search);
  const legacySearchQuery = searchParams.get("s")?.trim() || "";

  switch (pathname) {
    case "/":
      if (legacySearchQuery) {
        return null;
      }
      return mergeSeoOverride(seoData.home, "site:home");

    case "/super-deals":
      return mergeSeoOverride((
        seoData.superDeals.tags?.[searchParams.get("cat") || "All"] ||
        seoData.superDeals
      ), searchParams.get("cat") ? "" : "site:superDeals");

    case "/about-us":
      return mergeSeoOverride(seoData.about, "site:about");

    case "/contact-us":
      return mergeSeoOverride(seoData.contact, "site:contact");

    case "/privacy-policy":
      return mergeSeoOverride(seoData.privacyPolicy, "site:privacyPolicy");

    case "/cookies-policy":
      return mergeSeoOverride(seoData.cookiesPolicy, "site:cookiesPolicy");

    case "/terms-and-conditions":
      return mergeSeoOverride(seoData.termsConditions, "site:termsConditions");

    case "/affiliate-disclosure":
      return mergeSeoOverride(seoData.affiliate, "site:affiliate");

    case "/disclaimer":
      return mergeSeoOverride(seoData.disclaimer, "site:disclaimer");

    case "/latest-updates":
      return mergeSeoOverride(seoData.latestUpdates, "site:latestUpdates");

      

    case "/jobs":
    case "/jobs/":
      return mergeSeoOverride(seoData.jobs, "site:jobs");

    case "/search":
      return null;

    case "/wishlist-favorites":
      return mergeSeoOverride(seoData.wishlist, "site:wishlist");

    case "/visual-stories":
      return mergeSeoOverride(getWebStorySeoFallback(), "site:webStory");

    case "/tour-packages":
      return (
        seoData.tourPackages.tags?.[searchParams.get("section") || "All"] ||
        seoData.tourPackages
      );

      case "/advertise-with-us":
      return mergeSeoOverride(seoData.advertise, "site:advertise");

    // Dynamic routes handle their own SEO — return null so App never
    // overwrites their title with a generic fallback.
    default:
      return null;
  }
}

function getInitialLocation() {
  if (typeof window === "undefined") {
    return { pathname: "/", search: "" };
  }

  const embeddedRoute = getEmbeddedRoute();
  if (embeddedRoute) {
    return splitRoute(embeddedRoute);
  }

  const rawPathname = window.location.pathname || "/";
  const pathnamePart =
    routerBasename !== "/" && rawPathname.startsWith(routerBasename)
      ? rawPathname.slice(routerBasename.length) || "/"
      : rawPathname;

  return {
    pathname: pathnamePart.startsWith("/") ? pathnamePart : `/${pathnamePart}`,
    search: window.location.search,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Set <title> synchronously at module evaluation time — before React mounts,
// before the loading screen appears, before any paint. This is the single
// source of truth for the initial title and prevents any flash.
// ─────────────────────────────────────────────────────────────────────────────
const _initialLocation = getInitialLocation();
const _initialSeo = resolveSeo(_initialLocation.pathname, _initialLocation.search);
if (typeof document !== "undefined" && _initialSeo?.title) {
  document.title = _initialSeo.title;
}

// ─────────────────────────────────────────────────────────────────────────────
// App
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  const location     = useLocation();
  const showBottomAd = location.pathname !== "/super-deals";
  const legacySearchQuery = new URLSearchParams(location.search).get("s")?.trim() || "";
  const isLegacySearchPage = location.pathname === "/" && Boolean(legacySearchQuery);

  const [darkMode, setDarkMode] = useState(getInitialDarkMode);

  // ── Resolve SEO once per navigation — memoised so it never re-runs
  //    mid-render and never falls back to a generic "Travel RethinkWays" title ─
  const seo = useMemo(
    () => resolveSeo(location.pathname, location.search),
    [location.pathname, location.search]
  );

  // Keep <title> in sync on every client-side navigation (including during boot)
  useEffect(() => {
    if (seo?.title) {
      document.title = seo.title;
    }
    // Do NOT set a fallback here — dynamic-route pages manage their own title
  }, [seo]);

  // ── Footer encoding fix ────────────────────────────────────────────────────
  useEffect(() => {
    const copyrightFixes = [
      "\u00c2\u00a9",
      "Ãƒâ€šÃ‚Â©",
      "ÃƒÆ'Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â©",
    ];

    const normalizeFooterText = () => {
      document.querySelectorAll("footer *").forEach((node) => {
        if (
          node.childNodes.length !== 1 ||
          node.firstChild?.nodeType !== Node.TEXT_NODE
        ) return;

        const text = node.textContent ?? "";

        if (copyrightFixes.some((v) => text.includes(v))) {
          node.textContent = copyrightFixes.reduce(
            (t, v) => t.replaceAll(v, "\u00a9"),
            text
          );
        }

        if (text.trim().toUpperCase() === "SPONSORED AD") {
          node.classList.remove("text-white", "text-white/45", "text-black/45", "opacity-100");
          node.classList.add("text-black");
          node.style.opacity = "1";
        }
      });
    };

    normalizeFooterText();
    const observer = new MutationObserver(normalizeFooterText);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // ── Analytics ──────────────────────────────────────────────────────────────
  useEffect(() => {
    window.gtag?.("config", "G-8RDF7WJVFH", { page_path: location.pathname });
    window.fbq?.("track", "PageView");
    window.clarity?.("set", "page", location.pathname);
  }, [location.pathname]);

  // Full-screen loader disabled.
  // (Previously showed a boot LoadingScreen + Suspense fallbacks.)
  // Removed to render pages immediately.

  // ── Full app ───────────────────────────────────────────────────────────────
  return (
    <div className="trw-app">
      {seo && (
        <SEO
          title={seo.title}
          description={seo.description}
          canonical={seo.canonical}
          image={seo.image}
        />
      )}

      <Header darkMode={darkMode} onDarkModeChange={setDarkMode} />

      <main className="min-h-screen">
        <Routes>
          <Route path="/privacy-policy"       element={<PrivacyPolicy />} />
          <Route path="/cookies-policy"       element={<CookiesPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
          <Route path="/super-deals"          element={<SuperDeals darkMode={darkMode} />} />
          <Route path="/about-us"             element={<AboutUs />} />
          <Route path="/contact-us"           element={<ContactUs />} />
          <Route path="/super-deals-manager"  element={<SuperDealsManager />} />
          <Route path="/disclaimer"           element={<Disclaimer />} />
          <Route path="/tour-packages"        element={<TourPackagesPage darkMode={darkMode} />} />
          <Route path="/travel-tour-packages" element={<TourPackagesPage darkMode={darkMode} />} />
          <Route path="/tour-packages/:destinationSlug-tour-packages" element={<TourPackagesPage darkMode={darkMode} />} />
          <Route path="/:destinationSlug-tour-packages" element={<TourPackagesPage darkMode={darkMode} />} />
          <Route path="/visual-stories"       element={<WebStory />} />
          <Route path="/latest-updates"       element={<CategoryPage />} />
          <Route path="/search"               element={<SearchResults darkMode={darkMode} />} />
          <Route path="/category/:slug"       element={<CategoryPage />} />
          <Route path="/wishlist-favorites"   element={<Wishlistfavorites darkMode={darkMode} />} />
          <Route path="/advertise-with-us"      element={<AdvertiseWithUs />} />
          <Route path="/jobs/*"              element={<Jobs darkMode={darkMode} />} />
          <Route path="/"                     element={isLegacySearchPage ? <CategoryPage /> : <Home darkMode={darkMode} />} />
        </Routes>
      </main>

      {showBottomAd && (
        <div className="mx-auto mt-10 w-full max-w-[1500px] px-4">
          <AdShortcode shortcode="[trw_gd_brand_creative]" />
        </div>
      )}

      <Footer />
    </div>
  );
}

export default App;
